// Наш флот и владельцы судов. ТОЛЬКО ЧТЕНИЕ (ядро — fleet_api.py, ручки /fleet/*).
//
// ЧТО ЭТОТ ЭКРАН МОЖЕТ И ЧЕГО НЕ МОЖЕТ, И ЭТО ВИДНО СРАЗУ.
// Может: показать наш флот и владельца каждого судна, дать деловой контакт владельца
// (почта и телефон ЮРЛИЦА из карточки компании Bitrix), показать качество этой связи —
// у скольких судов владельца нет вовсе, у скольких владельцев некому позвонить, сколько
// карточек носят один и тот же IMO, — и показать список судов реестра РМРС, которых у
// нас не заведено.
// НЕ может: сказать, где судно сейчас и когда придёт в порт, и дать контакты чужого
// судовладельца. Ни одной AIS-координаты и ни одного контакта из MagicPort в зеркале
// нет — таких таблиц в базе не существует вовсе, а не «они пустые». Поэтому раздел
// называется «наш флот», а не «потенциальные клиенты»: список лидов, который зеркало
// может дать честно, — это только разрыв с реестром, и он короткий.
//
// ЧИСЛА НА ЭКРАНЕ НЕ ВБИТЫ НИ ОДНО: все до единого приходят из /fleet/refs и подставляются.
// Зеркало живое, синк идёт непрерывно, и вбитое число расходится с данными в тот же день —
// при сверке 28.07.2026 счётчик владельцев с почтой сдвинулся на единицу за полчаса между
// двумя замерами. Поэтому здесь нет ни одной цифры, набранной руками.
import { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'
import { Охват, Пейджер } from './RsVessels'

// ═══════════════════════════════ транспорт ═══════════════════════════════
// Свой разбор ответа, а не api/client.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра со своими фильтрами.
// Классы ошибок переиспользуем — заводить свои значило бы получить вторую обработку 401
// и вторую страницу логина.

const БАЗА = '/api/fleet'
const РАЗМЕР = 50                      // как PAGE в fleet_api.py

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица читалась бы как
    // «таких судов нет», а это ложь про чужой флот: поднимаем тот же тип, что и
    // остальные разделы, и QueryState печатает по нему честное объяснение.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

type Фильтры = Record<string, string | number | undefined>

function строкаЗапроса(ф: Фильтры): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    // Ноль — осмысленное значение фильтра «без порога», и он обязан уехать в ядро.
    // Проверка на undefined и '' вместо `if (!v)` именно поэтому: живой ноль в этом
    // ядре уже терялся, и терялся молча.
    if (v === undefined || v === '') continue
    p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─────────── формы ответов ядра (см. fleet_api.py) ───────────

interface Страница<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  note?: string
  _scope?: { limited?: boolean; partial?: boolean; note?: string }
}

interface Счётчики {
  counts: {
    vessels: number
    with_owner: number
    no_owner: number
    owners: number
    owners_with_email: number
    owners_with_phone: number
    owners_unreachable: number
    owners_no_human: number
    max_vessels_per_owner: number
  }
  imo: {
    ok: number
    missing: number
    malformed: number
    dup_groups: number
    dup_vessels: number
    bad_checksum: number
  }
  rs: { rows: number; with_imo: number; matched: number; gap: number }
  vessel_flag: Array<{ k: string; n: number }>
  vessel_port: Array<{ k: string; n: number }>
  empty_columns: { rs_vessels: string[] }
  synced: { vessels?: string; companies?: string; rs_synced?: string; rs_scraped?: string }
  notes: Record<string, string>
  page: number
  min_query: number
}

interface Владелец {
  company_id: number | null
  title: string
  vessels: number
  email: string | null
  phone: string | null
  has_human: boolean
}

interface Владение {
  company_id: number | null
  title: string
  email: string | null
  phone: string | null
}

interface Судно {
  vessel_id: number | null
  name: string
  imo: string | null
  imo_state: 'ok' | 'missing' | 'malformed'
  imo_checksum_ok: boolean | null
  imo_twins: number
  mmsi: string | null
  flag: string | null
  home_port: string | null
  owner: Владение | null
}

interface СудноРеестра {
  imo: string
  name: string
  flag: string | null
  port_of_registry: string | null
  call_sign: string | null
  rs_number: string | null
  source_url: string | null
  first_seen: string | null
  scraped_at: string | null
}

const общее = { retry: 1, staleTime: 5 * 60_000 } as const

const useСчётчики = () =>
  useQuery({
    queryKey: ['fleet', 'refs'],
    queryFn: () => запрос<Счётчики>('/refs'),
    staleTime: 30 * 60_000,
    retry: 1,
  })

const useВладельцы = (ф: Фильтры, включён: boolean) =>
  useQuery({
    queryKey: ['fleet', 'owners', ф],
    queryFn: () => запрос<Страница<Владелец>>('/owners' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    enabled: включён,
    ...общее,
  })

const useСуда = (ф: Фильтры, включён: boolean) =>
  useQuery({
    queryKey: ['fleet', 'vessels', ф],
    queryFn: () => запрос<Страница<Судно>>('/vessels' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    enabled: включён,
    ...общее,
  })

const useРазрыв = (ф: Фильтры, включён: boolean) =>
  useQuery({
    queryKey: ['fleet', 'gap', ф],
    queryFn: () => запрос<Страница<СудноРеестра>>('/gap' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    enabled: включён,
    ...общее,
  })

// ═══════════════════════════════ экран ═══════════════════════════════

type Вкладка = 'owners' | 'vessels' | 'gap'

export default function FleetOwners() {
  const [вкладка, setВкладка] = useState<Вкладка>('owners')
  const refs = useСчётчики()
  const с = refs.data

  return (
    <>
      <div className="page-head">
        <h1>Наш флот и владельцы</h1>
        <span className="muted">
          {с ? `судов: ${с.counts.vessels} · владельцев: ${с.counts.owners}` : ''}
        </span>
      </div>

      {/* Не мелкая оговорка внизу, а первое, что видит человек: он придёт сюда за
          позицией судна и контактами чужого судовладельца и должен сразу узнать, что
          этого в данных нет вовсе. */}
      <div className="notice">
        <b>Это наш флот, а не список чужих судов.</b>{' '}
        {с?.notes.ais ?? ''} {с?.notes.magicport ?? ''}
        <div className="hint" style={{ marginTop: 6 }}>
          {с?.notes.source ?? ''}
        </div>
      </div>

      {с ? <Плитки с={с} /> : null}

      {/* Переключатель списков сделан обычным полем со своей подписью, а не полосой
          вкладок: вёрстки вкладок в styles.css нет, и заводить её ради одного экрана
          значило бы поставить на страницу единственный в CRM элемент без стилей. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <label htmlFor="fleet-view">Что показать</label>{' '}
        <select id="fleet-view" value={вкладка}
                onChange={(e) => setВкладка(e.target.value as Вкладка)}>
          <option value="owners">владельцев и связь с ними</option>
          <option value="vessels">суда и их владельцев</option>
          <option value="gap">суда реестра, которых нет у нас</option>
        </select>
      </div>

      {вкладка === 'owners' && <СписокВладельцев с={с} />}
      {вкладка === 'vessels' && <СписокСудов с={с} />}
      {вкладка === 'gap' && <СписокРазрыва с={с} />}

      <Свежесть с={с} />
    </>
  )
}

/** Свежесть зеркала и — отдельно — свежесть обхода реестра. Это РАЗНЫЕ сроки, и второй
 *  важнее: разрыв с реестром стареет по дате обхода, а не по времени синка. */
function Свежесть({ с }: { с: Счётчики | undefined }) {
  if (!с) return null
  return (
    <div className="hint" style={{ marginTop: 12 }}>
      Зеркало Bitrix обновлено {fmtDate(с.synced.vessels, true)}.
      {' '}Реестр РМРС обойдён скрапером {fmtDate(с.synced.rs_scraped)}: разрыв с реестром
      стареет по этой дате, а не по времени синка.
    </div>
  )
}

function Плитки({ с }: { с: Счётчики }) {
  const н = с.counts
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="tiles">
        <div className="tile">
          <div className="t-label">Судов в зеркале</div>
          <div className="t-sum num">{н.vessels}</div>
          <div className="t-label">
            с владельцем — {н.with_owner}, без владельца — {н.no_owner}
          </div>
        </div>
        <div className="tile">
          <div className="t-label">Компаний-владельцев</div>
          <div className="t-sum num">{н.owners}</div>
          <div className="t-label">
            у самой крупной {pluralN(н.max_vessels_per_owner, 'судно', 'судна', 'судов')}
          </div>
        </div>
        <div className="tile">
          <div className="t-label">Из них с почтой</div>
          <div className="t-sum num">{н.owners_with_email}</div>
          <div className="t-label">с телефоном — {н.owners_with_phone}</div>
        </div>
        <div className="tile">
          <div className="t-label">Не с кем говорить</div>
          <div className="t-sum num">{н.owners_no_human}</div>
          <div className="t-label">
            владельцев без единого живого контакта; совсем без связи —
            {' '}{н.owners_unreachable}
          </div>
        </div>
        <div className="tile">
          <div className="t-label">Карточек с общим IMO</div>
          <div className="t-sum num">{с.imo.dup_vessels}</div>
          <div className="t-label">
            это {pluralN(с.imo.dup_groups, 'судно', 'судна', 'судов')}, заведённых дважды
            и больше
          </div>
        </div>
        <div className="tile">
          <div className="t-label">Есть в реестре, нет у нас</div>
          <div className="t-sum num">{с.rs.gap}</div>
          <div className="t-label">
            из {pluralN(с.rs.with_imo, 'судна', 'судов', 'судов')} реестра с настоящим IMO
          </div>
        </div>
      </div>
      <div className="hint" style={{ marginTop: 10 }}>
        IMO считается настоящим, если это <b>семь цифр</b>. Сейчас таких {с.imo.ok}
        {' '}из {с.counts.vessels}: пусто у {с.imo.missing}, непохоже на IMO
        {' '}у {с.imo.malformed} (там встречается девятизначный номер — это MMSI, а не IMO).
        {' '}Контрольную цифру не проходят {с.imo.bad_checksum} — их видно фильтром, но из
        выдачи они не выброшены: спрятать опечатку не значит её починить.
      </div>
    </div>
  )
}

// ═══════════════════════════════ владельцы ═══════════════════════════════

function СписокВладельцев({ с }: { с: Счётчики | undefined }) {
  const [q, setQ] = useState('')
  const [contact, setContact] = useState('')
  const [min, setMin] = useState('')
  const [sort, setSort] = useState('vessels')
  const [start, setStart] = useState(0)

  const фильтры = useMemo<Фильтры>(() => ({
    q: q.trim() || undefined,
    contact: contact || undefined,
    min: min || undefined,
    sort,
    start,
    limit: РАЗМЕР,
  }), [q, contact, min, sort, start])

  const запрос = useВладельцы(фильтры, true)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <Охват scope={данные?._scope} />

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="fleet-owners">
          <div className="f" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="foq">Название компании</label>
            <input id="foq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: ВОЛГА или МАРИН" />
          </div>

          <div className="f" style={{ minWidth: 240 }}>
            <label htmlFor="focontact">Как с ними связаться</label>
            <select id="focontact" value={contact}
                    onChange={(e) => сСброса(setContact)(e.target.value)}>
              <option value="">все владельцы</option>
              <option value="email">есть почта</option>
              <option value="phone">есть телефон</option>
              <option value="none">нет ни почты, ни телефона</option>
              <option value="nohuman">нет ни одного живого контакта</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="fomin">Судов у владельца, не меньше</label>
            <input id="fomin" type="number" min={0} value={min}
                   onChange={(e) => сСброса(setMin)(e.target.value)} placeholder="любое" />
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="fosort">Сортировка</label>
            <select id="fosort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="vessels">по числу судов</option>
              <option value="title">по названию</option>
              <option value="contact">сначала те, с кем есть связь</option>
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          Почта и телефон здесь — реквизиты <b>юрлица</b> из карточки компании Bitrix.
          «Нет ни одного живого контакта» означает, что у компании заведены только карточки
          судов и ни одного человека: письмо отправить есть куда, а спросить некого.
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="hint">
            {данные ? `найдено: ${данные.total}` : ''}
            {с ? ` · всего владельцев в зеркале: ${с.counts.owners}` : ''}
          </div>
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Владелец</th>
                  <th className="nowrap">Судов</th>
                  <th>Почта юрлица</th>
                  <th className="nowrap">Телефон юрлица</th>
                  <th>Живые контакты</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((в) => (
                  <СтрокаВладельца key={в.company_id ?? в.title} в={в} />
                ))}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty">
                        <div className="empty-title">Под эти условия владельцев нет</div>
                        <div className="empty-hint">
                          Владельцем здесь считается компания, к которой привязана хотя бы
                          одна карточка судна. Если компания есть в CRM, но её суда не
                          привязаны к ней в Bitrix, сюда она не попадёт — и починить это
                          можно только в Bitrix.
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching}
                   onStart={setStart} />
        </div>
      </QueryState>
    </>
  )
}

function СтрокаВладельца({ в }: { в: Владелец }) {
  return (
    <tr>
      <td data-label="Владелец">
        {в.company_id
          ? <EntityLink kind="company" id={в.company_id} title={в.title} />
          : fmtText(в.title)}
      </td>
      <td className="num nowrap" data-label="Судов">{в.vessels}</td>
      <td data-label="Почта юрлица">
        {в.email ? <a href={`mailto:${в.email}`}>{в.email}</a> : <span className="hint">не заполнена</span>}
      </td>
      <td className="nowrap" data-label="Телефон юрлица">
        {в.phone ? fmtText(в.phone) : <span className="hint">не заполнен</span>}
      </td>
      <td data-label="Живые контакты">
        {в.has_human
          ? 'есть человек в карточке'
          : <span className="hint">только карточки судов, людей нет</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ суда ═══════════════════════════════

function СписокСудов({ с }: { с: Счётчики | undefined }) {
  const [q, setQ] = useState('')
  const [flag, setFlag] = useState('')
  const [port, setPort] = useState('')
  const [owner, setOwner] = useState('')
  const [imo, setImo] = useState('')
  const [sort, setSort] = useState('name')
  const [start, setStart] = useState(0)

  const фильтры = useMemo<Фильтры>(() => ({
    q: q.trim() || undefined,
    flag: flag || undefined,
    port: port || undefined,
    owner: owner || undefined,
    imo: imo || undefined,
    sort,
    start,
    limit: РАЗМЕР,
  }), [q, flag, port, owner, imo, sort, start])

  const запрос = useСуда(фильтры, true)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <Охват scope={данные?._scope} />

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="fleet-vessels">
          <div className="f" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="fvq">Название судна, IMO или MMSI</label>
            <input id="fvq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: Русич или 9353046" />
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="fvflag">Флаг</label>
            <select id="fvflag" value={flag} onChange={(e) => сСброса(setFlag)(e.target.value)}>
              <option value="">любой</option>
              {(с?.vessel_flag ?? []).map((ф) => (
                <option key={ф.k} value={ф.k}>{ф.k || '(не указан)'} ({ф.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="fvport">Порт приписки</label>
            <select id="fvport" value={port} onChange={(e) => сСброса(setPort)(e.target.value)}>
              <option value="">любой</option>
              {(с?.vessel_port ?? []).map((п) => (
                <option key={п.k} value={п.k}>{п.k || '(не указан)'} ({п.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="fvowner">Владелец</label>
            <select id="fvowner" value={owner} onChange={(e) => сСброса(setOwner)(e.target.value)}>
              <option value="">все суда</option>
              <option value="yes">только с владельцем</option>
              <option value="no">только без владельца</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 210 }}>
            <label htmlFor="fvimo">Состояние IMO</label>
            <select id="fvimo" value={imo} onChange={(e) => сСброса(setImo)(e.target.value)}>
              <option value="">любое</option>
              <option value="ok">настоящий (семь цифр)</option>
              <option value="missing">не заполнен</option>
              <option value="malformed">не похож на IMO</option>
              <option value="dup">один IMO на несколько карточек</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="fvsort">Сортировка</label>
            <select id="fvsort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="name">по названию</option>
              <option value="imo">по IMO</option>
              <option value="owner">по владельцу</option>
              <option value="flag">по флагу</option>
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          {с?.notes.flags ?? ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="hint">
            {данные ? `найдено: ${данные.total}` : ''}
            {с ? ` · всего судов в зеркале: ${с.counts.vessels}` : ''}
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Судно</th>
                  <th className="nowrap">IMO</th>
                  <th className="nowrap">MMSI</th>
                  <th className="nowrap">Флаг</th>
                  <th>Порт приписки</th>
                  <th>Владелец</th>
                  <th>Связь с владельцем</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((судно) => (
                  <СтрокаСудна key={судно.vessel_id ?? `${судно.name}-${судно.imo ?? ''}`}
                               с={судно} />
                ))}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty">
                        <div className="empty-title">Под эти условия судов нет</div>
                        <div className="empty-hint">
                          Проверьте фильтр «состояние IMO»: у заметной части наших судов
                          номер не заполнен вовсе, и по IMO они не ищутся ни здесь, ни в
                          сверке с реестром.
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching}
                   onStart={setStart} />
        </div>
      </QueryState>
    </>
  )
}

const ИМО_СЛОВАМИ: Record<Судно['imo_state'], string> = {
  ok: '',
  missing: 'не заполнен',
  malformed: 'не похож на IMO',
}

function СтрокаСудна({ с }: { с: Судно }) {
  return (
    <tr>
      <td data-label="Судно">
        {с.vessel_id
          ? <EntityLink kind="vessel" id={с.vessel_id} title={с.name} />
          : fmtText(с.name)}
      </td>
      <td className="mono nowrap" data-label="IMO">
        {с.imo ? fmtText(с.imo) : <span className="hint">{ИМО_СЛОВАМИ[с.imo_state]}</span>}
        {с.imo_checksum_ok === false && (
          <div className="hint">контрольная цифра не сходится</div>
        )}
        {с.imo_twins > 1 && (
          <div className="hint">
            этот же IMO ещё у{' '}
            {pluralN(с.imo_twins - 1, 'карточки', 'карточек', 'карточек')}
          </div>
        )}
      </td>
      <td className="mono nowrap" data-label="MMSI">{fmtText(с.mmsi)}</td>
      <td className="nowrap" data-label="Флаг">{fmtText(с.flag)}</td>
      <td data-label="Порт приписки">{fmtText(с.home_port)}</td>
      <td data-label="Владелец">
        {с.owner?.company_id
          ? <EntityLink kind="company" id={с.owner.company_id} title={с.owner.title} />
          : <span className="hint">владелец не привязан в Bitrix</span>}
      </td>
      <td data-label="Связь с владельцем">
        {с.owner
          ? (
            <>
              {с.owner.email ? <div>{с.owner.email}</div> : null}
              {с.owner.phone ? <div className="nowrap">{с.owner.phone}</div> : null}
              {!с.owner.email && !с.owner.phone
                ? <span className="hint">у компании ни почты, ни телефона</span>
                : null}
            </>
          )
          : <span className="hint">некому писать: владельца нет</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ разрыв с реестром ═══════════════════════════════

function СписокРазрыва({ с }: { с: Счётчики | undefined }) {
  const [q, setQ] = useState('')
  const [flag, setFlag] = useState('')
  const [sort, setSort] = useState('name')
  const [start, setStart] = useState(0)

  const фильтры = useMemo<Фильтры>(() => ({
    q: q.trim() || undefined,
    flag: flag || undefined,
    sort,
    start,
    limit: РАЗМЕР,
  }), [q, flag, sort, start])

  const запрос = useРазрыв(фильтры, true)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <Охват scope={данные?._scope} />

      {/* Единственный список лидов, который зеркало может дать честно. Он короткий, и
          приукрашивать его нечем — поэтому сказано прямо, чего в нём нет. */}
      <div className="notice">
        <b>Это весь список лидов, который даёт зеркало.</b> Здесь суда, стоящие в реестре
        РМРС, для которых в Bitrix не заведено карточки. Владельца, почты и телефона у этих
        строк <b>не будет</b>: в зеркале реестра колонка владельца пуста во всех строках.
        Дальше — руками: открыть карточку судна в реестре по ссылке и искать судовладельца
        отдельно.
        {с?.empty_columns.rs_vessels.length ? (
          <div className="hint" style={{ marginTop: 6 }}>
            Пусты во всех{' '}
            {pluralN(с.rs.rows, 'строке', 'строках', 'строках')} зеркала реестра:{' '}
            {с.empty_columns.rs_vessels.join(', ')}. Поэтому этих колонок нет на экране —
            пустая колонка читается как «этого у судна нет».
          </div>
        ) : null}
        <div className="hint" style={{ marginTop: 6 }}>
          {с?.notes.gap ?? ''}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="fleet-gap">
          <div className="f" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="fgq">Название, IMO или регистровый номер</label>
            <input id="fgq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="в реестре названия латиницей: например SADKO" />
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="fgflag">Флаг по реестру</label>
            <input id="fgflag" value={flag} onChange={(e) => сСброса(setFlag)(e.target.value)}
                   placeholder="как в реестре: Russia" />
          </div>

          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="fgsort">Сортировка</label>
            <select id="fgsort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="name">по названию</option>
              <option value="imo">по IMO</option>
              <option value="flag">по флагу</option>
              <option value="seen">сначала недавно появившиеся</option>
            </select>
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="hint">
            {данные ? `найдено: ${данные.total}` : ''}
            {с ? ` · в реестре с настоящим IMO: ${с.rs.with_imo}, совпало: ${с.rs.matched}` : ''}
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Название в реестре</th>
                  <th className="nowrap">IMO</th>
                  <th className="nowrap">РС №</th>
                  <th className="nowrap">Позывной</th>
                  <th className="nowrap">Флаг</th>
                  <th>Порт приписки</th>
                  <th className="nowrap">Замечено</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((р) => <СтрокаРазрыва key={р.imo} р={р} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty">
                        <div className="empty-title">Разрыва с реестром под эти условия нет</div>
                        <div className="empty-hint">
                          Сверка идёт только по семизначному IMO и только с теми судами
                          реестра, у которых он есть. Судно реестра без IMO в этот список
                          не попадает вовсе: сверять его не с чем.
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching}
                   onStart={setStart} />
        </div>
      </QueryState>
    </>
  )
}

function СтрокаРазрыва({ р }: { р: СудноРеестра }) {
  return (
    <tr>
      <td data-label="Название в реестре">
        {р.source_url
          ? <a href={р.source_url} target="_blank" rel="noreferrer">{fmtText(р.name)}</a>
          : fmtText(р.name)}
      </td>
      <td className="mono nowrap" data-label="IMO">{fmtText(р.imo)}</td>
      <td className="mono nowrap" data-label="РС №">{fmtText(р.rs_number)}</td>
      <td className="mono nowrap" data-label="Позывной">{fmtText(р.call_sign)}</td>
      <td className="nowrap" data-label="Флаг">{fmtText(р.flag)}</td>
      <td data-label="Порт приписки">{fmtText(р.port_of_registry)}</td>
      <td className="nowrap" data-label="Замечено">{fmtDate(р.first_seen)}</td>
    </tr>
  )
}
